// src/components/layout/Footer/FooterUi.tsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Zap, ArrowRight, Facebook, Twitter, Instagram, Youtube } from 'lucide-react';
import { NAV_LINKS, FOOTER_PRODUCTS, FOOTER_SERVICES } from '../../../data/navData';
import { BRAND } from '../../../utils/constants';
import ContainerUi from '../../ui/Container/ContainerUi';
import { useThemeContext } from '../../../context/ThemeContext';

const FooterUi: React.FC = () => {
  const year = new Date().getFullYear();
  const { isDark } = useThemeContext();
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  return (
    <footer className="relative overflow-hidden" style={{ background: 'var(--bg-1)' }}>

      {/* Top gradient separator */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, var(--blue), var(--violet), transparent)' }} />

      {/* Glow orbs */}
      <div className="absolute bottom-0 left-1/4 w-96 h-96 glow-orb glow-blue opacity-5 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 glow-orb glow-violet opacity-5 pointer-events-none" />

      {/* Newsletter / CTA strip */}
      {pathname !== '/' && pathname !== '/contact' && (
        <div className="py-6" style={{ borderBottom: '1px solid var(--divider)' }}>
          <ContainerUi>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-display font-bold text-xl" style={{ color: 'var(--t1)' }}>
                  Ready to build something great?
                </h3>
                <p className="text-sm mt-1" style={{ color: 'var(--t3)' }}>
                  Talk to our engineers and get a free technical consultation.
                </p>
              </div>
              <Link to="/contact"
                className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white btn-gradient">
                Start a Project <ArrowRight size={14} />
              </Link>
            </div>
          </ContainerUi>
        </div>
      )}

      {/* Main grid */}
      <div className="py-6">
        <ContainerUi>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

            {/* Brand */}
            <div className="lg:col-span-1">
              <Link to="/" className="inline-flex items-center mb-5 group">
                <span className="relative block" style={{ height: "56px" }}>
                  <img
                    src="/brand/svg/foretek-logo-full-light.svg"
                    alt="Foretek Solution"
                    width="1570"
                    height="315"
                    className="w-auto object-contain transition-all duration-300 group-hover:scale-[1.02]"
                    style={{
                      height: "56px",
                      opacity: isDark ? 0 : 1,
                      position: isDark ? "absolute" : "relative",
                      top: 0,
                      left: 0,
                      pointerEvents: isDark ? "none" : "auto",
                    }}
                    loading="lazy"
                  />
                  <img
                    src="/brand/svg/foretek-logo-full-dark.svg"
                    alt="Foretek Solution"
                    width="1570"
                    height="315"
                    className="w-auto object-contain transition-all duration-300 group-hover:scale-[1.02]"
                    style={{
                      height: "56px",
                      opacity: isDark ? 1 : 0,
                      position: isDark ? "relative" : "absolute",
                      top: 0,
                      left: 0,
                      pointerEvents: isDark ? "auto" : "none",
                    }}
                    loading="lazy"
                  />
                </span>
              </Link>
              <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--t3)' }}>
                Enterprise-grade software development, embedded systems, and automation solutions for businesses worldwide.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  { Icon: MapPin, val: BRAND.address, href: null },
                  { Icon: Mail, val: BRAND.email, href: `mailto:${BRAND.email}` },
                  // Phone number temporarily hidden
                  // { Icon: Phone,  val: BRAND.phone,    href: `tel:${BRAND.phone.replace(/\s/g, '')}`   },
                ].map(({ Icon, val, href }) => (
                  <div key={val} className="flex items-start gap-2.5">
                    <Icon size={13} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--blue)' }} />
                    {href ? (
                      <a href={href} className="text-xs leading-relaxed transition-colors footer-link" style={{ color: 'var(--t3)' }}>{val}</a>
                    ) : (
                      <span className="text-xs leading-relaxed" style={{ color: 'var(--t3)' }}>{val}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Social Media Links */}
              <div className="flex items-center gap-3 mt-6">
                {[
                  { Icon: Facebook, href: "https://www.facebook.com/profile.php?id=61592237356675", label: "Facebook", color: "#1877F2" },
                  { Icon: Instagram, href: "https://www.instagram.com/foreteksolution/?hl=en", label: "Instagram", color: "#E1306C" },
                ].map(({ Icon, href, label, color }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 border hover:scale-110"
                    style={{
                      borderColor: "var(--divider)",
                      background: "rgba(255,255,255,0.02)",
                      color: "var(--t3)",
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = color;
                      (e.currentTarget as HTMLElement).style.color = color;
                      (e.currentTarget as HTMLElement).style.backgroundColor = `${color}10`;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = "var(--divider)";
                      (e.currentTarget as HTMLElement).style.color = "var(--t3)";
                      (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.02)";
                    }}
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-display font-semibold text-sm mb-5 uppercase tracking-wider" style={{ color: 'var(--t1)' }}>
                Services
              </h4>
              <ul className="flex flex-col gap-2.5">
                {FOOTER_SERVICES.map(s => (
                  <li key={s}>
                    <Link to="/services"
                      className="text-sm footer-link-premium"
                      style={{ color: 'var(--t3)' }}>
                      {s}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products */}
            <div>
              <h4 className="font-display font-semibold text-sm mb-5 uppercase tracking-wider" style={{ color: 'var(--t1)' }}>
                Products
              </h4>
              <ul className="flex flex-col gap-2.5">
                {FOOTER_PRODUCTS.slice(0, 6).map(p => (
                  <li key={p}>
                    <Link to="/products"
                      className="text-sm footer-link-premium"
                      style={{ color: 'var(--t3)' }}>
                      {p}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-display font-semibold text-sm mb-5 uppercase tracking-wider" style={{ color: 'var(--t1)' }}>
                Company
              </h4>
              <ul className="flex flex-col gap-2.5">
                {NAV_LINKS.map(link => (
                  <li key={link.path}>
                    <Link to={link.path}
                      className="text-sm footer-link-premium"
                      style={{ color: 'var(--t3)' }}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ContainerUi>
      </div>

      {/* Bottom bar */}
      <div className="py-4" style={{ borderTop: '1px solid var(--divider)' }}>
        <ContainerUi className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs" style={{ color: 'var(--t4)' }}>
            © {year} <strong>FORETEK SOLUTION AND SERVICES (OPC) PVT LTD.</strong> All rights reserved.
          </p>
          <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--t4)' }}>
            <Zap size={10} style={{ color: 'var(--blue)' }} />
            Committed to keep you forward
          </div>
        </ContainerUi>
      </div>
    </footer>
  );
};

export default FooterUi;
